import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, ResponsiveContainer } from 'recharts';
import useAuth from '../hooks/useAuth';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

const F1Dashboard = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meeting, setMeeting] = useState(null);
  const [session, setSession] = useState(null);
  const [nextSession, setNextSession] = useState(null);
  const [positions, setPositions] = useState([]);
  const [laps, setLaps] = useState([]);
  const [pitStops, setPitStops] = useState([]);
  const [stints, setStints] = useState([]);
  const [carData, setCarData] = useState([]);
  const [drivers, setDrivers] = useState({});
  const [teams, setTeams] = useState({});
  const [countdown, setCountdown] = useState('');
  const [showTelemetry, setShowTelemetry] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [offSeason, setOffSeason] = useState(false);
  const [forceRaceMode, setForceRaceMode] = useState(false);
  const [standings, setStandings] = useState({ driver: [], constructor: [] });
  const [lastRace, setLastRace] = useState(null);

  const year = new Date().getFullYear();

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch race schedule from Jolpica API (free Ergast replacement)
      // Use current season data
      const scheduleRes = await fetch(`https://api.jolpi.ca/ergast/f1/current.json`);

      if (!scheduleRes.ok) {
        throw new Error(`Failed to fetch race schedule: ${scheduleRes.status}`);
      }

      const scheduleData = await scheduleRes.json();

      if (!scheduleData?.MRData?.RaceTable?.Races) {
        throw new Error('Invalid race schedule data');
      }

      const races = scheduleData.MRData.RaceTable.Races;
      const now = new Date();

      // Find current/next race and check for active sessions
      let currentRace = null;
      let currentSession = null;
      let nextRace = null;

      for (const race of races) {
        const sessions = [
          { name: 'FirstPractice', ...race.FirstPractice },
          { name: 'SecondPractice', ...race.SecondPractice },
          race.ThirdPractice && { name: 'ThirdPractice', ...race.ThirdPractice },
          { name: 'Qualifying', ...race.Qualifying },
          race.Sprint && { name: 'Sprint', ...race.Sprint },
          race.SprintQualifying && { name: 'SprintQualifying', ...race.SprintQualifying },
          { name: 'Race', ...race }
        ].filter(Boolean);

        for (const session of sessions) {
          const sessionDate = new Date(session.date + 'T' + session.time);
          // Use realistic session durations
          let sessionDuration = 60 * 60 * 1000; // 1 hour default
          if (session.name === 'Race') sessionDuration = 2 * 60 * 60 * 1000; // 2 hours for race
          if (session.name === 'Sprint') sessionDuration = 30 * 60 * 1000; // 30 minutes for sprint

          const sessionEnd = new Date(sessionDate.getTime() + sessionDuration);

          if (sessionDate <= now && now < sessionEnd) {
            // This session is currently active
            currentRace = race;
            currentSession = session;
            break;
          }
        }

        if (currentRace) break;

        // Check for next upcoming session
        if (!nextRace) {
          const raceDate = new Date(race.date + 'T' + race.time);
          if (raceDate > now) {
            nextRace = race;
          }
        }
      }

      if (currentRace && currentSession) {
        // There's an active session
        setMeeting({
          meeting_name: currentRace.raceName,
          circuit_id: currentRace.Circuit.circuitId,
          country: currentRace.Circuit.Location.country,
          date_start: currentRace.date
        });

        // Set session type
        const sessionTypeMap = {
          'FirstPractice': 'FP1',
          'SecondPractice': 'FP2',
          'ThirdPractice': 'FP3',
          'Qualifying': 'Q',
          'SprintQualifying': 'SQ',
          'Sprint': 'S',
          'Race': 'R'
        };

        setSession({ type: sessionTypeMap[currentSession.name] || currentSession.name });
      } else if (nextRace) {
        // Show next race info
        setMeeting({
          meeting_name: nextRace.raceName,
          circuit_id: nextRace.Circuit.circuitId,
          country: nextRace.Circuit.Location.country,
          date_start: nextRace.date
        });
        setNextSession({
          date_start: nextRace.date + 'T' + nextRace.time,
          type: 'Race'
        });
      }

      // Always fetch championship data
      await fetchOffSeasonData();

      // Generate demo data for race mode if we have a current race
      if (currentRace && currentRace.type !== 'Finished') {
        setOffSeason(false);
        const { fakePositions, fakeLaps, fakePitStops, fakeStints, fakeTelemetry } = generateFakeRaceData();
        setPositions(fakePositions);
        setLaps(fakeLaps);
        setPitStops(fakePitStops);
        setStints(fakeStints);
        setCarData(fakeTelemetry);
      } else {
        setOffSeason(true);
      }

    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err.message);
      // Fallback to championship data only
      setOffSeason(true);
      await fetchOffSeasonData();
    } finally {
      setLoading(false);
    }
  }, [year]);

  const refreshDemoData = useCallback(() => {
    // Refresh demo data if in race mode
    if (!offSeason) {
      const { fakePositions, fakeLaps, fakePitStops, fakeStints, fakeTelemetry } = generateFakeRaceData();
      setPositions(fakePositions);
      setLaps(fakeLaps);
      setPitStops(fakePitStops);
      setStints(fakeStints);
      setCarData(fakeTelemetry);
    }
    console.log('Demo data refreshed');
  }, [offSeason]);

  const updateCountdown = useCallback(() => {
    if (!nextSession) return;
    const now = new Date();
    const target = new Date(nextSession.date_start);
    const diff = target - now;
    if (diff <= 0) {
      setCountdown('Started!');
      return;
    }
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    const secs = Math.floor((diff % 60000) / 1000);
    setCountdown(`${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`);
  }, [nextSession]);

  const fetchOffSeasonData = useCallback(async () => {
    try {
      console.log('Fetching championship data via Firebase proxy...');

      // Fetch driver standings
      const driverResponse = await fetch(`https://getf1championshipdata-3ogooxwlda-uc.a.run.app?year=${year}&type=standings`);
      const driverData = await driverResponse.json();

      // Fetch constructor standings
      const constructorResponse = await fetch(`https://getf1championshipdata-3ogooxwlda-uc.a.run.app?year=${year}&type=constructorStandings`);
      const constructorData = await constructorResponse.json();

      // Fetch last race results
      const lastRaceResponse = await fetch(`https://getf1championshipdata-3ogooxwlda-uc.a.run.app?year=${year}&type=lastResults`);
      const lastRaceData = await lastRaceResponse.json();

      if (driverData.success && constructorData.success) {
        setStandings({
          driver: driverData.data || [],
          constructor: constructorData.data || []
        });
        setLastRace(lastRaceData.success ? lastRaceData.data : null);
        console.log('Championship data loaded successfully');
      } else {
        throw new Error('Failed to fetch championship data');
      }
    } catch (err) {
      console.error('Off-season data error:', err);
      // Set empty data to show error message
      setStandings({ driver: [], constructor: [] });
      setLastRace(null);
    }
  }, [year]);

  const fetchStaticData = useCallback(async () => {
    try {
      const [driversRes, teamsRes] = await Promise.all([
        fetch('https://api.openf1.org/v1/drivers'),
        fetch('https://api.openf1.org/v1/teams')
      ]);

      // Handle responses with error checking
      if (driversRes.ok) {
        const driversData = await driversRes.json();
        if (Array.isArray(driversData)) {
          setDrivers(Object.fromEntries(driversData.map(d => [d.driver_number, d])));
        }
      }
      if (teamsRes.ok) {
        const teamsData = await teamsRes.json();
        if (Array.isArray(teamsData)) {
          setTeams(Object.fromEntries(teamsData.map(t => [t.team_id, t])));
        }
      }
    } catch (err) {
      console.error('Static data error:', err);
    }
  }, []);

  const getSessionType = (type) => {
    const types = { 'Practice': 'P', 'Qualifying': 'Q', 'Sprint': 'S', 'Race': 'R' };
    return types[type] || type;
  };

  const refreshData = () => {
    refreshDemoData();
  };

  // Generate fake demo data for race mode preview
  const generateFakeRaceData = () => {
    const fakeDrivers = [
      { number: 1, name: 'VER', team: 'Red Bull', color: '#1E41FF' },
      { number: 11, name: 'PER', team: 'Red Bull', color: '#1E41FF' },
      { number: 16, name: 'LEC', team: 'Ferrari', color: '#E8002D' },
      { number: 55, name: 'SAI', team: 'Ferrari', color: '#E8002D' },
      { number: 4, name: 'NOR', team: 'McLaren', color: '#FF8000' },
      { number: 81, name: 'PIA', team: 'McLaren', color: '#FF8000' },
      { number: 63, name: 'RUS', team: 'Mercedes', color: '#27F4D2' },
      { number: 44, name: 'HAM', team: 'Mercedes', color: '#27F4D2' },
      { number: 14, name: 'ALO', team: 'Aston Martin', color: '#229971' },
      { number: 18, name: 'STR', team: 'Aston Martin', color: '#229971' }
    ];

    // Fake positions data
    const fakePositions = fakeDrivers.map((driver, index) => ({
      driver_number: driver.number,
      position: index + 1,
      gap_to_leader: index === 0 ? null : (Math.random() * 5 + 0.5 * index).toFixed(3),
      x: 30 + Math.random() * 40, // Random x coordinate for track map
      y: 30 + Math.random() * 40, // Random y coordinate for track map
      team_id: Math.floor(index / 2) + 1 // Assign team IDs
    }));

    // Fake laps data - more laps to demonstrate the widget
    const fakeLaps = [];
    for (let lap = 35; lap <= 55; lap++) {
      fakeDrivers.forEach(driver => {
        fakeLaps.push({
          driver_number: driver.number,
          lap_number: lap,
          lap_duration: (85 + Math.random() * 10).toFixed(3)
        });
      });
    }

    // Fake pit stops data
    const fakePitStops = [
      { driver_number: 1, lap_number: 25, pit_duration: 22.5 },
      { driver_number: 16, lap_number: 32, pit_duration: 21.8 },
      { driver_number: 4, lap_number: 28, pit_duration: 23.1 },
      { driver_number: 63, lap_number: 35, pit_duration: 22.3 }
    ];

    // Fake stints data (tyre compounds)
    const fakeStints = [
      { driver_number: 1, compound: 'SOFT', stint_number: 1 },
      { driver_number: 11, compound: 'MEDIUM', stint_number: 1 },
      { driver_number: 16, compound: 'SOFT', stint_number: 1 },
      { driver_number: 55, compound: 'MEDIUM', stint_number: 1 },
      { driver_number: 4, compound: 'HARD', stint_number: 1 },
      { driver_number: 81, compound: 'MEDIUM', stint_number: 1 },
      { driver_number: 63, compound: 'SOFT', stint_number: 1 },
      { driver_number: 44, compound: 'HARD', stint_number: 1 },
      { driver_number: 14, compound: 'MEDIUM', stint_number: 1 },
      { driver_number: 18, compound: 'SOFT', stint_number: 1 }
    ];

    // Fake telemetry data
    const fakeTelemetry = [];
    for (let i = 0; i < 50; i++) {
      fakeTelemetry.push({
        date: new Date(Date.now() - (50 - i) * 1000).toISOString(),
        speed: 280 + Math.random() * 40
      });
    }

    return { fakePositions, fakeLaps, fakePitStops, fakeStints, fakeTelemetry };
  };

  const toggleRaceMode = () => {
    setForceRaceMode(!forceRaceMode);
    if (!forceRaceMode) {
      // Switching to race mode - generate fake data
      const { fakePositions, fakeLaps, fakePitStops, fakeStints, fakeTelemetry } = generateFakeRaceData();
      setPositions(fakePositions);
      setLaps(fakeLaps);
      setPitStops(fakePitStops);
      setStints(fakeStints);
      setCarData(fakeTelemetry);
      setDrivers(fakePositions.reduce((acc, pos) => {
        const driver = [
          { number: 1, name: 'VER', team: 'Red Bull' },
          { number: 11, name: 'PER', team: 'Red Bull' },
          { number: 16, name: 'LEC', team: 'Ferrari' },
          { number: 55, name: 'SAI', team: 'Ferrari' },
          { number: 4, name: 'NOR', team: 'McLaren' },
          { number: 81, name: 'PIA', team: 'McLaren' },
          { number: 63, name: 'RUS', team: 'Mercedes' },
          { number: 44, name: 'HAM', team: 'Mercedes' },
          { number: 14, name: 'ALO', team: 'Aston Martin' },
          { number: 18, name: 'STR', team: 'Aston Martin' }
        ].find(d => d.number === pos.driver_number);
        acc[pos.driver_number] = {
          driver_number: driver.number,
          name_acronym: driver.name
        };
        return acc;
      }, {}));
      setTeams({
        1: { team_name: 'Red Bull' },
        2: { team_name: 'Ferrari' },
        3: { team_name: 'McLaren' },
        4: { team_name: 'Mercedes' },
        5: { team_name: 'Aston Martin' }
      });
    } else {
      // Switching back to off-season mode - clear fake data
      setPositions([]);
      setLaps([]);
      setPitStops([]);
      setStints([]);
      setCarData([]);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);



  // Countdown timer
  useEffect(() => {
    if (!nextSession) return;
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [nextSession, updateCountdown]);

  const trackPaths = {
    1: 'M 50 10 Q 90 10 90 50 Q 90 90 50 90 Q 10 90 10 50 Q 10 10 50 10 Z', // Mexico
    2: 'M 20 20 L 80 20 L 80 40 L 60 40 L 60 60 L 40 60 L 40 80 L 20 80 Z' // Brazil
  };

  const teamColors = { 1: '#FF0000', 2: '#00FF00' }; // Example

  const layout = [
    { i: 'track', x: 0, y: 0, w: 6, h: 4 },
    { i: 'positions', x: 6, y: 0, w: 6, h: 4 },
    { i: 'laps', x: 0, y: 4, w: 6, h: 4 },
    { i: 'pits', x: 6, y: 4, w: 6, h: 4 },
    ...(showTelemetry ? [{ i: 'telemetry', x: 0, y: 8, w: 12, h: 4 }] : [])
  ];

  if (loading) return <div style={{ background: '#000', color: '#00FF41', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;
  if (error) return <div style={{ background: '#000', color: '#FF0000', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{error}</div>;

  return (
    <div style={{
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      color: '#ffffff',
      fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      minHeight: '100vh',
      padding: '24px'
    }}>
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
      }}>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h1 style={{
                margin: '0 0 8px 0',
                fontSize: '2.5rem',
                fontWeight: '700',
                background: 'linear-gradient(45deg, #e10600, #ff6b35)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {meeting?.meeting_name || 'F1 Dashboard'}
              </h1>
              <p style={{
                margin: '0',
                fontSize: '1.1rem',
                color: '#b0b0b0',
                fontWeight: '500'
              }}>
                {session ? `🏁 Ongoing: ${meeting?.meeting_name} ${session.type}` : nextSession ? `⏰ Next: ${meeting?.meeting_name} ${nextSession.type} - Starts in ${countdown}` : '📅 No upcoming sessions'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              {offSeason && (
                <button
                  onClick={toggleRaceMode}
                  style={{
                    background: forceRaceMode ? 'linear-gradient(45deg, #ff6b35, #f7931e)' : 'linear-gradient(45deg, #4ade80, #22c55e)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: forceRaceMode ? '0 4px 15px rgba(255, 107, 53, 0.3)' : '0 4px 15px rgba(74, 222, 128, 0.3)'
                  }}
                  onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                >
                  {forceRaceMode ? '🏆 Off-Season' : '🏁 Race Mode'}
                </button>
              )}
              <button
                onClick={refreshData}
                style={{
                  background: 'linear-gradient(45deg, #e10600, #ff6b35)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '12px 24px',
                  fontSize: '0.9rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 15px rgba(225, 6, 0, 0.3)'
                }}
                onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
              >
                🔄 Refresh Data
              </button>
            </div>
          </div>
          {forceRaceMode && (
            <div style={{
              background: 'linear-gradient(45deg, #ff6b35, #f7931e)',
              border: '1px solid rgba(255, 107, 53, 0.3)',
              borderRadius: '8px',
              padding: '12px 16px',
              textAlign: 'center',
              color: '#ffffff',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}>
              🏁 <strong>Live Session Detected:</strong> {meeting?.meeting_name} {session?.type} is currently running. OpenF1 API (formerly free) now requires authentication during live sessions, so showing simulated data to demonstrate the interface.
            </div>
          )}
        </div>
      </div>

      {offSeason && !forceRaceMode ? (
        <div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            <h2 style={{
              margin: '0 0 16px 0',
              fontSize: '2rem',
              fontWeight: '700',
              background: 'linear-gradient(45deg, #ff6b35, #f7931e)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              🏆 Off-Season Mode
            </h2>
            <p style={{ margin: '0', color: '#b0b0b0', fontSize: '1.1rem' }}>
              Championship standings and last race results
            </p>
          </div>

          {standings.driver.length === 0 && standings.constructor.length === 0 ? (
            <div style={{
              background: 'rgba(255, 107, 53, 0.1)',
              border: '1px solid rgba(255, 107, 53, 0.3)',
              borderRadius: '12px',
              padding: '24px',
              textAlign: 'center',
              color: '#ff6b35'
            }}>
              <h3 style={{ margin: '0 0 8px 0' }}>⚠️ Data Unavailable</h3>
              <p style={{ margin: '0', color: '#b0b0b0' }}>
                No championship data available. The Ergast API may be temporarily unavailable.
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}>
                <h3 style={{
                  margin: '0 0 20px 0',
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: '#4ade80'
                }}>
                  🏎️ Driver Standings
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.9rem'
                  }}>
                    <thead>
                      <tr style={{
                        borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                        textAlign: 'left'
                      }}>
                        <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Pos</th>
                        <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Driver</th>
                        <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Team</th>
                        <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {standings.driver.slice(0, 10).map((d, index) => (
                        <tr key={d.Driver.driverId} style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          backgroundColor: index < 3 ? 'rgba(255, 215, 0, 0.1)' : 'transparent'
                        }}>
                          <td style={{ padding: '12px 8px', fontWeight: '500' }}>{d.position}</td>
                          <td style={{ padding: '12px 8px', fontWeight: '500' }}>
                            {d.Driver.givenName} {d.Driver.familyName}
                          </td>
                          <td style={{ padding: '12px 8px', color: '#b0b0b0' }}>{d.Constructors[0].name}</td>
                          <td style={{ padding: '12px 8px', fontWeight: '600', color: '#4ade80' }}>{d.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
              }}>
                <h3 style={{
                  margin: '0 0 20px 0',
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: '#4ade80'
                }}>
                  🏭 Constructor Standings
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.9rem'
                  }}>
                    <thead>
                      <tr style={{
                        borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                        textAlign: 'left'
                      }}>
                        <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Pos</th>
                        <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Team</th>
                        <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {standings.constructor.slice(0, 10).map((c, index) => (
                        <tr key={c.Constructor.constructorId} style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          backgroundColor: index < 3 ? 'rgba(255, 215, 0, 0.1)' : 'transparent'
                        }}>
                          <td style={{ padding: '12px 8px', fontWeight: '500' }}>{c.position}</td>
                          <td style={{ padding: '12px 8px', fontWeight: '500' }}>{c.Constructor.name}</td>
                          <td style={{ padding: '12px 8px', fontWeight: '600', color: '#4ade80' }}>{c.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {lastRace && (
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <h3 style={{
                margin: '0 0 20px 0',
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#4ade80'
              }}>
                🏁 Last Race: {lastRace.raceName}
              </h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.9rem'
                }}>
                  <thead>
                    <tr style={{
                      borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                      textAlign: 'left'
                    }}>
                      <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Pos</th>
                      <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Driver</th>
                      <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Team</th>
                      <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Gap</th>
                      <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Interval</th>
                      <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lastRace.Results.map((r, index) => {
                      // Calculate points based on position
                      const pointsSystem = [25, 18, 15, 12, 10, 8, 6, 4, 2, 1];
                      const points = r.position <= 10 ? pointsSystem[r.position - 1] : '';

                      // Gap column: Show "Winner" for position 1, gap to winner for others
                      let gapDisplay = '';
                      if (parseInt(r.position) === 1) {
                        gapDisplay = 'Winner';
                      } else {
                        // Check if driver finished with lap deficit (+1 Lap, +2 Laps, etc.)
                        const winnerLaps = lastRace.Results[0]?.laps || 0;
                        const driverLaps = r.laps || 0;
                        const lapDeficit = winnerLaps - driverLaps;

                        if (lapDeficit > 0) {
                          gapDisplay = `+${lapDeficit} Lap${lapDeficit > 1 ? 's' : ''}`;
                        } else if (r.Time?.time) {
                          gapDisplay = r.Time.time;
                        } else {
                          gapDisplay = r.status || 'Unknown';
                        }
                      }

                      // Interval column: Show "Winner" for position 1, gap to previous driver for others
                      let interval = '';
                      if (parseInt(r.position) === 1) {
                        interval = 'Winner';
                      } else if (index > 0) {
                        const prevResult = lastRace.Results[index - 1];
                        if (r.Time?.time && prevResult.Time?.time) {
                          // Both finished - calculate gap to previous driver
                          const parseTimeToSeconds = (timeStr) => {
                            if (timeStr.startsWith('+')) {
                              const cleanTime = timeStr.substring(1).replace('s', '');
                              if (cleanTime.includes(':')) {
                                const [mins, secs] = cleanTime.split(':');
                                return parseInt(mins) * 60 + parseFloat(secs);
                              }
                              return parseFloat(cleanTime);
                            }
                            return 0;
                          };

                          const currentGap = parseTimeToSeconds(r.Time.time);
                          const prevGap = parseTimeToSeconds(prevResult.Time.time);
                          const gapToPrev = currentGap - prevGap;

                          if (gapToPrev > 0) {
                            if (gapToPrev >= 60) {
                              const mins = Math.floor(gapToPrev / 60);
                              const secs = (gapToPrev % 60).toFixed(3);
                              interval = `+${mins}:${secs.padStart(6, '0')}s`;
                            } else {
                              interval = `+${gapToPrev.toFixed(3)}s`;
                            }
                          } else {
                            interval = '+0.000s';
                          }
                        } else if (r.Time?.time && !prevResult.Time?.time) {
                          // Current finished, previous DNF - show current driver's gap to winner
                          interval = r.Time.time;
                        } else if (!r.Time?.time && prevResult.Time?.time) {
                          // Previous finished, current DNF
                          interval = r.status || 'DNF';
                        } else {
                          // Both DNF
                          interval = r.status || 'DNF';
                        }
                      }

                      return (
                        <tr key={r.Driver.driverId} style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                          backgroundColor: r.position <= 3 ? 'rgba(255, 215, 0, 0.1)' : 'transparent'
                        }}>
                          <td style={{ padding: '12px 8px', fontWeight: '500' }}>{r.position}</td>
                          <td style={{ padding: '12px 8px', fontWeight: '500' }}>
                            {r.Driver.givenName} {r.Driver.familyName}
                          </td>
                          <td style={{ padding: '12px 8px', color: '#b0b0b0' }}>{r.Constructor.name}</td>
                          <td style={{ padding: '12px 8px', fontWeight: '500' }}>{gapDisplay}</td>
                          <td style={{ padding: '12px 8px', fontWeight: '500' }}>{interval}</td>
                          <td style={{ padding: '12px 8px', fontWeight: '600', color: '#4ade80' }}>{points}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <ResponsiveGridLayout layouts={{ lg: layout }} breakpoints={{ lg: 1200 }} cols={{ lg: 12 }}>
          <div key="track" style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#4ade80'
            }}>
              🗺️ Track Map
            </h3>
            <div style={{
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <svg width="100%" height="250" viewBox="0 0 100 100" style={{ maxWidth: '400px' }}>
                <path
                  d={trackPaths[meeting?.circuit_id] || 'M 10 10 L 90 10 L 90 90 L 10 90 Z'}
                  fill="none"
                  stroke="#4ade80"
                  strokeWidth="2"
                />
                {positions.slice(0, 10).map(p => {
                  const x = typeof p.x === 'number' && !isNaN(p.x) ? p.x * 0.01 : 50;
                  const y = typeof p.y === 'number' && !isNaN(p.y) ? p.y * 0.01 : 50;
                  return (
                    <circle
                      key={p.driver_number}
                      cx={x}
                      cy={y}
                      r="3"
                      fill={teamColors[p.team_id] || '#ffffff'}
                      stroke="#000"
                      strokeWidth="1"
                      title={`${drivers[p.driver_number]?.name_acronym || p.driver_number}`}
                    />
                  );
                })}
              </svg>
            </div>
          </div>

          <div key="positions" style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#4ade80'
            }}>
              🏁 Driver Positions
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.9rem'
              }}>
                <thead>
                  <tr style={{
                    borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                    textAlign: 'left'
                  }}>
                    <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Pos</th>
                    <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Driver</th>
                    <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Team</th>
                    <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Gap</th>
                    <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Tyre</th>
                    <th style={{ padding: '12px 8px', fontWeight: '600', color: '#e10600' }}>Pits</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.slice(0, 10).map((p, index) => {
                    // Get current tyre type from stints data
                    const driverStints = stints.filter(s => s.driver_number === p.driver_number);
                    const currentStint = driverStints[driverStints.length - 1];
                    const tyreType = currentStint?.compound || 'Unknown';

                    // Count pit stops for this driver
                    const driverPitStops = pitStops.filter(pit => pit.driver_number === p.driver_number);
                    const pitStopCount = driverPitStops.length;

                    return (
                      <tr key={p.driver_number} style={{
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        backgroundColor: index < 3 ? 'rgba(255, 215, 0, 0.1)' : 'transparent'
                      }}>
                        <td style={{ padding: '12px 8px', fontWeight: '500' }}>{p.position}</td>
                        <td style={{ padding: '12px 8px', fontWeight: '500' }}>
                          {drivers[p.driver_number]?.name_acronym || p.driver_number}
                        </td>
                        <td style={{ padding: '12px 8px', color: '#b0b0b0' }}>
                          {teams[p.team_id]?.team_name || p.team_id}
                        </td>
                        <td style={{ padding: '12px 8px', fontWeight: '500' }}>
                          {p.gap_to_leader ? `+${p.gap_to_leader}s` : '-'}
                        </td>
                        <td style={{ padding: '12px 8px', fontWeight: '600', color: '#4ade80' }}>
                          {tyreType}
                        </td>
                        <td style={{ padding: '12px 8px', fontWeight: '500' }}>
                          {pitStopCount}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div key="laps" style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#4ade80'
            }}>
              ⏱️ Lap Times
            </h3>
            <div style={{ marginBottom: '20px' }}>
              <select
                value={selectedDriver}
                onChange={e => setSelectedDriver(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(0, 0, 0, 0.5)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '0.9rem'
                }}
              >
                <option value="" style={{ background: 'rgba(0, 0, 0, 0.8)', color: '#ffffff' }}>All Drivers</option>
                {Object.values(drivers).map(d => (
                  <option key={d.driver_number} value={d.driver_number} style={{ background: 'rgba(0, 0, 0, 0.8)', color: '#ffffff' }}>
                    {d.name_acronym}
                  </option>
                ))}
              </select>
            </div>
            <div style={{
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '8px',
              padding: '16px',
              height: 'calc(100% - 80px)', // Scale with widget height
              overflowY: 'auto'
            }}>
              {laps.filter(l => !selectedDriver || l.driver_number === parseInt(selectedDriver)).slice(-10).length > 0 ? (
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.8rem'
                }}>
                  <thead>
                    <tr style={{
                      borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                      textAlign: 'left'
                    }}>
                      <th style={{ padding: '8px 4px', fontWeight: '600', color: '#e10600' }}>Lap</th>
                      <th style={{ padding: '8px 4px', fontWeight: '600', color: '#e10600' }}>Driver</th>
                      <th style={{ padding: '8px 4px', fontWeight: '600', color: '#e10600' }}>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {laps.filter(l => !selectedDriver || l.driver_number === parseInt(selectedDriver)).slice(-10).map((lap, index) => (
                      <tr key={`${lap.driver_number}-${lap.lap_number}`} style={{
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <td style={{ padding: '8px 4px', fontWeight: '500' }}>{lap.lap_number}</td>
                        <td style={{ padding: '8px 4px', fontWeight: '500' }}>
                          {drivers[lap.driver_number]?.name_acronym || lap.driver_number}
                        </td>
                        <td style={{ padding: '8px 4px', fontWeight: '600', color: '#4ade80' }}>
                          {lap.lap_duration}s
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{
                  textAlign: 'center',
                  color: '#b0b0b0',
                  padding: '20px'
                }}>
                  No lap time data available
                </div>
              )}
            </div>
          </div>

          <div key="pits" style={{
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#4ade80'
            }}>
              🔧 Pit Stops
            </h3>
            <div style={{
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '8px',
              padding: '16px',
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              {pitStops.slice(0, 10).length > 0 ? (
                pitStops.slice(0, 10).map((p, index) => (
                  <div key={index} style={{
                    padding: '8px 0',
                    borderBottom: index < pitStops.slice(0, 10).length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ color: '#b0b0b0' }}>
                      Lap {p.lap_number}
                    </span>
                    <span style={{ fontWeight: '600', color: '#4ade80' }}>
                      {p.pit_duration}s
                    </span>
                  </div>
                ))
              ) : (
                <div style={{
                  textAlign: 'center',
                  color: '#b0b0b0',
                  padding: '20px'
                }}>
                  No pit stops recorded
                </div>
              )}
            </div>
          </div>

          {showTelemetry && (
            <div key="telemetry" style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              gridColumn: 'span 12'
            }}>
              <h3 style={{
                margin: '0 0 20px 0',
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#4ade80'
              }}>
                📊 Telemetry Data
              </h3>
              <div style={{
                width: '100%',
                height: '250px',
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#b0b0b0'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '8px' }}>📈</div>
                  <div>Telemetry visualization</div>
                  <div style={{ fontSize: '0.8rem', marginTop: '4px' }}>
                    {carData.slice(-20).length} data points
                  </div>
                </div>
              </div>
            </div>
          )}
        </ResponsiveGridLayout>
      )}
      <div style={{ textAlign: 'center', marginTop: '32px' }}>
        <button
          onClick={() => setShowTelemetry(!showTelemetry)}
          style={{
            background: 'linear-gradient(45deg, #4ade80, #22c55e)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '12px',
            padding: '16px 32px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 15px rgba(74, 222, 128, 0.3)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
          onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
        >
          {showTelemetry ? '👁️ Hide' : '📊 Show'} Telemetry Data
        </button>
      </div>
    </div>
  );
};

export default F1Dashboard;
